import React, {useEffect, useMemo} from "react";

export type ParsedElementFromTextWithIndex = {
    element: string,
    startIndexOfStartTag: number,
    endIndexOfStartTag: number,
    startIndexOfEndTag: number | undefined | null,
    endIndexOfEndTag: number | undefined | null,
    children: ParsedElementFromTextWithIndex[],
    attributes: {[key: string]: string}
}

export type ParsedElementFromText = {
    element: string,
    children: ParsedElementFromTextWithIndex[],
    attributes: {[key: string]: string}
}

export const parseTemplateToHtml = (template: string, startKeyChar = "${{", endKeyChar = "}}", keyDelimiter = ";"): string => {

    let currentIndex = 0;
    while(true) {
        const searchIndex = template.substring(currentIndex).indexOf(startKeyChar);
        if (searchIndex === -1) break;
        currentIndex += searchIndex;

        const endOfKeyIndex = currentIndex + template.substring(currentIndex).indexOf(endKeyChar);
        const key = template.substring(currentIndex+startKeyChar.length, endOfKeyIndex );
        const name = key.split(keyDelimiter)[0];
        const attributesString = `name="${name}" ` + key.split(keyDelimiter)[1];

        template = template.substring(0, currentIndex) + `<input ${attributesString}/>` + template.substring(endOfKeyIndex+endKeyChar.length);
        currentIndex = currentIndex + 1;
    }
    return template;
}

export const extractTagsFromTemplate = (template: string) => {
    let elements: ParsedElementFromTextWithIndex[] = [];

    let currentIndex = 0;
    while (true) {

        let searchIndex = currentIndex+ template.substring(currentIndex).indexOf("<");
        if (searchIndex === -1 || searchIndex +1 === currentIndex) {
            break;
        }

        let searchIndex2 = currentIndex + template.substring(currentIndex).indexOf("</");
        let searchedIndex = Math.min(searchIndex, searchIndex2);
        const innerText = template.substring(currentIndex, searchedIndex);
        const text = {
            element: "",
            startIndexOfStartTag: currentIndex,
            endIndexOfStartTag: currentIndex,
            startIndexOfEndTag: searchedIndex-1,
            endIndexOfEndTag: searchedIndex-1,
            children: [],
            attributes: {
                innerText
            }
        }
        if (searchIndex === searchIndex2) {

            // tag closing
            const openElements = elements.filter(element => element.startIndexOfEndTag === undefined);
            if (openElements.length === 0) {
                continue;
            }
            const closingElement = openElements.length ===1 ? openElements[0] : openElements
                .reduce((element1, element2) =>
                    element1.endIndexOfStartTag > element2.endIndexOfStartTag ? element1 : element2);

            closingElement.startIndexOfEndTag = searchIndex;
            const endIndexOfEndTag = searchIndex + template.substring(searchIndex).indexOf(">");
            closingElement.endIndexOfEndTag = endIndexOfEndTag;
            currentIndex = endIndexOfEndTag+1;
        } else {
            // new tag opened
            const startIndexOfStartTag = searchIndex;
            const endIndexOfStartTag = searchIndex + template.substring(searchIndex).indexOf(">");
            const searchForSelfClosing = searchIndex + template.substring(searchIndex).indexOf("/>");

            const openingTag = template.substring(startIndexOfStartTag, endIndexOfStartTag + 1);
            const element = openingTag.substring(1).split(" ")[0].split("/")[0].split(">")[0];

            let startIndexOfEndTag = undefined;
            let endIndexOfEndTag = undefined;
            let selfClosing = false;
            if (searchForSelfClosing + 1 === endIndexOfStartTag) {
                startIndexOfEndTag = null;
                endIndexOfEndTag = null;
                selfClosing = true;
            }

            const attributes: {[key: string]: string} = {};

            let attributesString = openingTag.split(" ")
                .filter((_, i) => i !== 0)
                .join(" ");

            attributesString = attributesString.length === 0 ? attributesString : attributesString.substring(0, attributesString.length - (selfClosing ? 2 : 1))

            let attributeIndex = 0;
            while(true) {
                const equalIndex = attributeIndex + attributesString.substring(attributeIndex).indexOf("=");
                if (equalIndex === -1) {
                    break;
                }

                const firstSpeechMarkIndex = equalIndex + attributesString.substring(equalIndex).indexOf('"');
                if (firstSpeechMarkIndex === -1) {
                    break;
                }

                const secondSpeechMarkIndex = firstSpeechMarkIndex + attributesString.substring(firstSpeechMarkIndex+1).indexOf('"') + 1;
                if (firstSpeechMarkIndex === secondSpeechMarkIndex) {
                    break;
                }

                const attributeKey = attributesString.substring(attributeIndex, equalIndex);
                const attributeValue = attributesString.substring(firstSpeechMarkIndex+1, secondSpeechMarkIndex);

                attributes[attributeKey.trim()] = attributeValue.trim();

                attributeIndex = secondSpeechMarkIndex+1;
            }

            elements.push({
                element: element as any,
                startIndexOfStartTag,
                endIndexOfStartTag,
                startIndexOfEndTag,
                endIndexOfEndTag,
                children: [],
                attributes: attributes
            })
            currentIndex = endIndexOfStartTag + 1;
        }
        if (innerText !== "") {
            elements.push(text);
        }
    }

    return elements;
}

export const assignChildrenRecursively = (elements: ParsedElementFromTextWithIndex[]): ParsedElementFromTextWithIndex[] => {
    if (elements.length === 0) {
        return [];
    }
    if (elements.length === 1) {
        return [elements[0]];
    } else {
        let parentElements: ParsedElementFromTextWithIndex[] = [];
        let remainingElements : ParsedElementFromTextWithIndex[] = elements;
        while (true) {
            const parentElement = remainingElements
                .reduce((element1, element2) =>
                    element1.endIndexOfStartTag < element2.endIndexOfStartTag ? element1 : element2
                )
            parentElements.push(parentElement);
            const elementsWhichFitInParentElement = remainingElements.filter(element =>
                element.startIndexOfStartTag > parentElement.endIndexOfStartTag &&
                (element.endIndexOfEndTag || element.endIndexOfStartTag) < (parentElement.startIndexOfEndTag || parentElement.endIndexOfStartTag));
            // if 0, add text as between start and end.
            parentElement.children = assignChildrenRecursively(elementsWhichFitInParentElement);
            remainingElements = remainingElements
                .filter(element => !(element.startIndexOfStartTag === parentElement.startIndexOfStartTag) &&
                    !(elementsWhichFitInParentElement.includes(element))
                )
            if (remainingElements.length === 0 ) {
                break;
            }
        }

        return parentElements;
    }
}

export const createJsx = (elements: ParsedElementFromText[], onKeyValueChange:(key: string, value: string) => void, values : { [key: string]: string }): JSX.Element[] => {
    const createElement = (element:ParsedElementFromText): JSX.Element => {
        let {style, ...attributes} = element.attributes;
        let styleObject = {};
        if (style !== undefined) {
             styleObject = style.split(" ").reduce((accumulator, current) => {
                const key = current.split(":")[0];
                const value = current.split(":")[1];
                // @ts-ignore
                 accumulator[key] = value;
                return accumulator;
            }, {});
        };
        if (element.element === "br") {
            return <br style={styleObject} {...attributes}/>
        }
        if (element.element === "input") {
            const name = element.attributes.name;
            return <input style={styleObject} {...attributes}
                          value={values[name] || ""}
                          required={true}
                          onChange={(event) => {
                              const value = event.currentTarget.value;
                              onKeyValueChange(name, value);
                          }
            }/>
        }
        if (element.element === "") {
            return <>{element.attributes.innerText}</>
        }
        if (element.element === "script") {
            return <></>
        }
        return React.createElement(element.element, {style:styleObject, ...attributes}, createJsx(element.children, onKeyValueChange, values))
    }

    return elements.map((element, i) =>
        <React.Fragment key={i}>{createElement(element)}</React.Fragment>);
}

const ApplicationMessageBodyParser = ({template, onKeyValueChange, values, onReadyChange}: {template: string, onKeyValueChange:(key: string, value: string) => void, values : { [key: string]: string }, onReadyChange: (ready: boolean) => void}) => {


    const flatElementsFromTemplate = useMemo(() => {
        const modifiedTemplate = parseTemplateToHtml(template);
        return extractTagsFromTemplate(modifiedTemplate);
    }, [template])

    const hierarchicalElementsFromTemplate = useMemo(() => assignChildrenRecursively(flatElementsFromTemplate), [flatElementsFromTemplate]);


    useEffect(() => {
        const numOfInputs = flatElementsFromTemplate.filter(element => element.element === "input").length;

        const scopeValues: { [key: string]: string; } = JSON.parse(JSON.stringify(values));
        const doNumOfValueMatchInputs = Object.values(scopeValues).filter(val => val.length > 0).length === numOfInputs;
        onReadyChange(doNumOfValueMatchInputs);
    }, [values, flatElementsFromTemplate, onReadyChange])

    return <>{createJsx(hierarchicalElementsFromTemplate, onKeyValueChange, values)}</>;
}

export default ApplicationMessageBodyParser;
